import Image from "next/image";
import Link from "next/link";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  ChevronRight,
  XCircle,
} from "lucide-react";
import { HomeHeader } from "@/components/home/HomeHeader";
import { HomeFooter } from "@/components/home/HomeFooter";
import { HomeWhatsappFab } from "@/components/home/HomeWhatsappFab";
import { HomePricing } from "@/components/home/HomePricing";
import {
  HomeCard,
  SectionContainer,
  SectionHeading,
  SectionShell,
} from "@/components/home/homeUi";
import {
  AGENDA_FAQ,
  AGENDA_FEATURES,
  AGENDA_LANDING_H1,
  AGENDA_LANDING_NAV,
  AGENDA_SECTION_IDS,
  BARBER_BENEFITS,
  CUSTOMER_EXPERIENCE,
  HOW_IT_WORKS,
  PAIN_POINTS,
  PRIMARY_CTA_HREF,
  PRIMARY_CTA_LABEL,
  PUBLIC_BOOKING_EXAMPLE,
} from "./agendaLandingContent";

function AgendaHero() {
  return (
    <Box
      as="section"
      position="relative"
      py={{ base: 14, md: 20, lg: 24 }}
      overflow="hidden"
      bg="barber.400"
    >
      <Box
        position="absolute"
        inset="0"
        bgGradient="radial(circle at 15% 25%, rgba(251, 169, 49, 0.14) 0%, transparent 50%)"
        pointerEvents="none"
        aria-hidden
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <HStack spacing={2} mb={6} fontSize="sm" color="barber.100" flexWrap="wrap">
          <Link href="/">
            <Text _hover={{ color: "orange.900" }}>Início</Text>
          </Link>
          <ChevronRight size={14} />
          <Text color="orange.900">Agenda online</Text>
        </HStack>

        <Grid
          templateColumns={{ base: "1fr", lg: "1.05fr 0.95fr" }}
          gap={{ base: 10, lg: 12 }}
          alignItems="center"
        >
          <GridItem>
            <VStack align="start" spacing={6}>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg="blackAlpha.400"
                color="orange.900"
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Agendamento online
              </Badge>

              <Heading
                as="h1"
                size={{ base: "2xl", md: "3xl" }}
                lineHeight="shorter"
                color="white"
              >
                {AGENDA_LANDING_H1}
              </Heading>

              <Text fontSize={{ base: "lg", md: "xl" }} color="barber.100">
                Organize horários da sua equipe e ofereça um link para clientes
                agendarem serviços online — sem depender só de mensagens e
                anotações.
              </Text>

              <HStack spacing={3} flexWrap="wrap" w="full">
                <Button
                  as={Link}
                  href={PRIMARY_CTA_HREF}
                  size="lg"
                  bg="button.cta"
                  color="black"
                  rightIcon={<ArrowRight size={16} />}
                  _hover={{ opacity: 0.85 }}
                >
                  {PRIMARY_CTA_LABEL}
                </Button>
                <Button
                  as={Link}
                  href={`#${AGENDA_SECTION_IDS.comoFunciona}`}
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.400"
                  color="white"
                  _hover={{ bg: "whiteAlpha.100" }}
                >
                  Ver como funciona
                </Button>
              </HStack>

              <Text fontSize="sm" color="barber.100">
                Teste grátis por 7 dias · Sem instalar aplicativo
              </Text>
            </VStack>
          </GridItem>

          <GridItem>
            <Flex justify="center">
              <Box
                position="relative"
                w="full"
                maxW={{ base: "280px", sm: "320px", lg: "360px" }}
                borderRadius="28px"
                border="1px solid"
                borderColor="whiteAlpha.250"
                overflow="hidden"
                bg="barber.900"
                boxShadow="0 28px 60px -16px rgba(251, 169, 49, 0.28), 0 16px 40px rgba(0, 0, 0, 0.45)"
              >
                <Image
                  src="/images/agendamento-online.jpeg"
                  alt="Tela de agendamento online do Meu Barbeiro PRO"
                  width={739}
                  height={1600}
                  priority
                  sizes="(max-width: 768px) 280px, 360px"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Flex>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}

function AgendaPainSection() {
  return (
    <SectionShell alt>
      <SectionContainer>
        <SectionHeading
          eyebrow="Situação atual"
          title="Marcação manual consome tempo e deixa a agenda confusa"
          description="Quando horários ficam espalhados, fica mais difícil organizar o dia e atender com tranquilidade."
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} maxW="4xl" mx="auto">
          {PAIN_POINTS.map((item) => (
            <HStack
              key={item}
              align="start"
              spacing={3}
              bg="barber.400"
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="xl"
              p={4}
            >
              <XCircle size={18} color="#fba931" style={{ flexShrink: 0, marginTop: 2 }} />
              <Text color="barber.100" fontSize="sm">
                {item}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaHowItWorks() {
  return (
    <SectionShell id={AGENDA_SECTION_IDS.comoFunciona}>
      <SectionContainer>
        <SectionHeading
          eyebrow="Como funciona"
          title="Da configuração ao agendamento na agenda"
          description="Configure sua operação no painel e deixe o cliente marcar horário pelo link público."
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {HOW_IT_WORKS.map((step) => (
            <HomeCard key={step.step} h="full">
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="full"
                bg="orange.900"
                color="black"
                fontWeight="bold"
                mb={4}
              >
                {step.step}
              </Flex>
              <Heading as="h3" size="md" color="white" mb={3}>
                {step.title}
              </Heading>
              <Text color="barber.100" fontSize="sm">
                {step.description}
              </Text>
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaFeatures() {
  return (
    <SectionShell id={AGENDA_SECTION_IDS.recursos} alt>
      <SectionContainer>
        <SectionHeading
          eyebrow="Recursos da agenda"
          title="Tudo que você precisa para organizar horários e receber agendamentos"
          description="Funcionalidades reais do Meu Barbeiro PRO, com foco em agenda e agendamento online."
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {AGENDA_FEATURES.map(({ title, description, icon: Icon }) => (
            <HomeCard key={title} h="full">
              <Box color="orange.900" mb={4}>
                <Icon size={28} />
              </Box>
              <Heading as="h3" size="md" color="white" mb={3}>
                {title}
              </Heading>
              <Text color="barber.100" fontSize="sm">
                {description}
              </Text>
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaBenefits() {
  return (
    <SectionShell id={AGENDA_SECTION_IDS.beneficios}>
      <SectionContainer>
        <SectionHeading
          eyebrow="Para a barbearia"
          title="Mais clareza na agenda, menos improviso no atendimento"
        />

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4} maxW="5xl" mx="auto">
          {BARBER_BENEFITS.map((benefit) => (
            <HStack
              key={benefit}
              align="start"
              spacing={3}
              bg="barber.900"
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="xl"
              p={4}
            >
              <CheckCircle size={18} color="#fba931" style={{ flexShrink: 0, marginTop: 2 }} />
              <Text color="barber.100" fontSize="sm">
                {benefit}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaCustomerExperience() {
  return (
    <SectionShell id={AGENDA_SECTION_IDS.experienciaCliente} alt>
      <SectionContainer>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={10} alignItems="center">
          <Box>
            <SectionHeading
              align="left"
              eyebrow="Para o cliente"
              title="Seus clientes agendam pelo celular, quando quiserem"
              description="Cada barbearia pode disponibilizar um acesso público de agendamento."
            />
            <List spacing={3}>
              {CUSTOMER_EXPERIENCE.map((item) => (
                <ListItem key={item} color="barber.100">
                  <ListIcon as={CheckCircle} color="orange.900" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>

          <HomeCard>
            <HStack spacing={2} mb={2} color="orange.900">
              <Calendar size={18} />
              <Text fontSize="sm" fontWeight="bold">
                Exemplo de link público
              </Text>
            </HStack>
            <Text color="white" fontWeight="semibold" mb={2}>
              {PUBLIC_BOOKING_EXAMPLE}
            </Text>
            <Text color="barber.100" fontSize="sm" mb={4}>
              O cliente informa o telefone, escolhe serviço, barbeiro (quando
              houver), data e horário. Você acompanha tudo na agenda do painel.
            </Text>
            <Button
              as={Link}
              href="/"
              variant="link"
              color="orange.900"
              fontSize="sm"
              rightIcon={<ArrowRight size={14} />}
            >
              Conheça o sistema completo
            </Button>
          </HomeCard>
        </Grid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaShowcase() {
  return (
    <SectionShell>
      <SectionContainer>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={10} alignItems="center">
          <Box
            position="relative"
            w="full"
            maxW="420px"
            mx={{ base: "auto", lg: 0 }}
            borderRadius="2xl"
            border="1px solid"
            borderColor="whiteAlpha.150"
            overflow="hidden"
            bg="barber.900"
          >
            <Image
              src="/images/tela02.jpeg"
              alt="Agenda do painel Meu Barbeiro PRO com horários organizados"
              width={739}
              height={1600}
              loading="lazy"
              sizes="(max-width: 768px) 100vw, 420px"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>

          <Box>
            <SectionHeading
              align="left"
              eyebrow="Na prática"
              title="Visualize horários e agendamentos em um painel claro"
              description="A agenda do painel ajuda a acompanhar o dia e organizar atendimentos por profissional."
            />
            <Text color="barber.100" fontSize="sm">
              Screenshots reais do produto. Sem promessas de recursos que ainda
              não existem — apenas o que você pode usar hoje.
            </Text>
          </Box>
        </Grid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaGetStarted() {
  return (
    <SectionShell alt>
      <SectionContainer>
        <SectionHeading
          eyebrow="Comece agora"
          title="Coloque sua agenda online em poucos passos"
          description="Crie sua conta, configure serviços e barbeiros, e compartilhe o link de agendamento."
        />

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} maxW="5xl" mx="auto">
          {[
            {
              step: "1",
              title: "Crie sua conta",
              description: "Cadastre-se e comece com teste grátis de 7 dias.",
            },
            {
              step: "2",
              title: "Configure a operação",
              description: "Cadastre serviços, barbeiros e organize a disponibilidade.",
            },
            {
              step: "3",
              title: "Compartilhe o link",
              description: "Envie o acesso de agendamento para seus clientes.",
            },
          ].map((item) => (
            <HomeCard key={item.step} textAlign="center">
              <Text
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w={10}
                h={10}
                borderRadius="full"
                bg="orange.900"
                color="black"
                fontWeight="bold"
                mb={4}
              >
                {item.step}
              </Text>
              <Heading as="h3" size="md" color="white" mb={3}>
                {item.title}
              </Heading>
              <Text color="barber.100" fontSize="sm">
                {item.description}
              </Text>
            </HomeCard>
          ))}
        </SimpleGrid>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaPricingSection() {
  return <HomePricing />;
}

function AgendaFaqSection() {
  return (
    <SectionShell id={AGENDA_SECTION_IDS.faq}>
      <SectionContainer>
        <SectionHeading
          eyebrow="FAQ"
          title="Dúvidas sobre agenda e agendamento online"
          description="Respostas objetivas sobre como funciona a agenda do Meu Barbeiro PRO."
        />

        <Box maxW="3xl" mx="auto">
          <HomeCard p={{ base: 2, md: 4 }}>
            <Accordion allowToggle reduceMotion>
              {AGENDA_FAQ.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  borderColor="whiteAlpha.100"
                  borderTop={index === 0 ? "none" : undefined}
                >
                  <h3>
                    <AccordionButton py={4} _hover={{ bg: "whiteAlpha.50" }}>
                      <Box flex="1" textAlign="left" fontWeight="semibold" color="white">
                        {item.question}
                      </Box>
                      <AccordionIcon color="orange.900" />
                    </AccordionButton>
                  </h3>
                  <AccordionPanel pb={4} color="barber.100" fontSize="sm">
                    {item.answer}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </HomeCard>

          <Text textAlign="center" mt={6} color="barber.100" fontSize="sm">
            Mais perguntas sobre o sistema?{" "}
            <Button
              as={Link}
              href="/home/faq"
              variant="link"
              color="orange.900"
              fontSize="sm"
            >
              Ver FAQ completo
            </Button>
          </Text>
        </Box>
      </SectionContainer>
    </SectionShell>
  );
}

function AgendaFinalCta() {
  return (
    <Box as="section" py={{ base: 16, md: 20 }} bg="orange.900">
      <Container maxW="container.xl">
        <VStack spacing={6} textAlign="center">
          <Heading as="h2" size={{ base: "xl", md: "2xl" }} color="black">
            Pronto para organizar sua agenda online?
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} maxW="2xl" color="blackAlpha.800">
            Teste grátis por 7 dias e veja como fica mais simples receber
            agendamentos na sua barbearia.
          </Text>
          <HStack spacing={4} flexWrap="wrap" justify="center">
            <Button
              as={Link}
              href={PRIMARY_CTA_HREF}
              size="lg"
              bg="black"
              color="white"
              rightIcon={<ArrowRight size={16} />}
              _hover={{ opacity: 0.85 }}
            >
              {PRIMARY_CTA_LABEL}
            </Button>
            <Button
              as={Link}
              href="/"
              size="lg"
              variant="outline"
              borderColor="black"
              color="black"
              _hover={{ bg: "blackAlpha.100" }}
            >
              Ver sistema completo
            </Button>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
}

export function AgendaLandingView() {
  return (
    <Box minH="100vh" bg="barber.400" color="gray.100">
      <HomeHeader nav={AGENDA_LANDING_NAV} />

      <Box as="main">
        <AgendaHero />
        <AgendaPainSection />
        <AgendaHowItWorks />
        <AgendaFeatures />
        <AgendaBenefits />
        <AgendaCustomerExperience />
        <AgendaShowcase />
        <AgendaGetStarted />
        <AgendaPricingSection />
        <AgendaFaqSection />
        <AgendaFinalCta />
      </Box>

      <HomeFooter />
      <HomeWhatsappFab />
    </Box>
  );
}
